import {
  DEFAULT_PHONE_COUNTRY,
  isValidMobileNumber,
  type PhoneCountryCode,
} from "@project/lib/mobile-number";

export interface VisitorInquiryPayload {
  visit_date: string;
  visitor_name: string;
  date_of_birth?: string;
  married_status?: boolean;
  marriage_anniversary_date?: string;
  age?: number;
  occupation?: string;
  mobile_number?: string;
  residence_phone?: string;
  email?: string;
  address?: string;
  business_address?: string;
  reason_for_visit?: string;
  visited_before: boolean;
  reference_source?: string;
  referrer_name?: string;
  referrer_mobile?: string;
}

export interface VisitorInquiryRecord extends VisitorInquiryPayload {
  id: number;
  created_at?: string;
  updated_at?: string;
}

export type VisitorInquiryValidationKey =
  | "dateRequired"
  | "nameRequired"
  | "mobileInvalid"
  | "residencePhoneInvalid"
  | "emailInvalid"
  | "ageInvalid"
  | "referrerMobileInvalid";

export const VISITOR_INQUIRY_VALIDATION_MESSAGES: Record<
  VisitorInquiryValidationKey,
  string
> = {
  dateRequired: "Please select the visit date.",
  nameRequired: "Please enter the visitor name.",
  mobileInvalid: "Enter a valid mobile number for the selected country.",
  residencePhoneInvalid: "Enter a valid residence phone number.",
  emailInvalid: "Enter a valid email address.",
  ageInvalid: "Enter a valid age between 1 and 120.",
  referrerMobileInvalid:
    "Enter a valid referrer mobile for the selected country.",
};

const PHONE_RE = /^\d{6,15}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function trimmed(value: unknown): string | undefined {
  const v = String(value ?? "").trim();
  return v ? v : undefined;
}

function optionalNumber(value: unknown): number | undefined {
  if (value === "" || value === undefined || value === null) {
    return undefined;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function digitsOnly(value: unknown): string {
  return String(value ?? "").replace(/\D/g, "");
}

export type VisitorInquiryFormState = {
  id?: number;
  date: string;
  name: string;
  date_of_birth?: string;
  married_status?: boolean;
  marriage_anniversary_date?: string;
  age?: number;
  occupation?: string;
  mobile_number?: string;
  residence_phone?: string;
  email?: string;
  address?: string;
  business_address?: string;
  reason_for_visit?: string;
  has_visited_before: boolean;
  previous_visit_details?: string;
  reference_source?: string;
  referrer_name?: string;
  referrer_mobile?: string;
  mobile_country?: PhoneCountryCode;
  referrer_mobile_country?: PhoneCountryCode;
};

export function visitorInquiryDefaultValues(): Partial<VisitorInquiryFormState> {
  return {
    date: new Date().toISOString().slice(0, 10),
    date_of_birth: "",
    married_status: false,
    marriage_anniversary_date: "",
    has_visited_before: false,
    previous_visit_details: "",
    mobile_country: DEFAULT_PHONE_COUNTRY,
    referrer_mobile_country: DEFAULT_PHONE_COUNTRY,
  };
}

/** Maps API record → VisitorFormFields inquiry form keys. */
export function mapVisitorInquiryToForm(
  record: VisitorInquiryRecord
): VisitorInquiryFormState {
  return {
    id: record.id,
    date: record.visit_date ?? "",
    name: record.visitor_name ?? "",
    date_of_birth: record.date_of_birth ?? "",
    married_status: Boolean(record.married_status),
    marriage_anniversary_date: record.marriage_anniversary_date ?? "",
    age: record.age,
    occupation: record.occupation ?? "",
    mobile_number: record.mobile_number ?? "",
    residence_phone: record.residence_phone ?? "",
    email: record.email ?? "",
    address: record.address ?? "",
    business_address: record.business_address ?? "",
    reason_for_visit: record.reason_for_visit ?? "",
    has_visited_before: Boolean(record.visited_before),
    previous_visit_details: "",
    reference_source: record.reference_source ?? "",
    referrer_name: record.referrer_name ?? "",
    referrer_mobile: record.referrer_mobile ?? "",
    mobile_country: DEFAULT_PHONE_COUNTRY,
    referrer_mobile_country: DEFAULT_PHONE_COUNTRY,
  };
}

/** Validates inquiry form state (same field keys as VisitorFormFields). */
export function validateVisitorInquiryForm(
  form: Record<string, unknown>
): VisitorInquiryValidationKey | null {
  if (!String(form.date ?? "").trim()) {
    return "dateRequired";
  }
  if (!String(form.name ?? "").trim()) {
    return "nameRequired";
  }

  const mobileCountry =
    (form.mobile_country as PhoneCountryCode | undefined) ??
    DEFAULT_PHONE_COUNTRY;
  if (
    String(form.mobile_number ?? "").trim() &&
    !isValidMobileNumber(String(form.mobile_number), mobileCountry, {
      required: false,
    })
  ) {
    return "mobileInvalid";
  }

  const residencePhone = digitsOnly(form.residence_phone);
  if (
    String(form.residence_phone ?? "").trim() &&
    !PHONE_RE.test(residencePhone)
  ) {
    return "residencePhoneInvalid";
  }

  const email = String(form.email ?? "").trim();
  if (email && !EMAIL_RE.test(email)) {
    return "emailInvalid";
  }

  if (form.age !== undefined && form.age !== null && form.age !== "") {
    const age = Number(form.age);
    if (!Number.isFinite(age) || age < 1 || age > 120) {
      return "ageInvalid";
    }
  }

  const referrerCountry =
    (form.referrer_mobile_country as PhoneCountryCode | undefined) ??
    DEFAULT_PHONE_COUNTRY;
  if (
    String(form.referrer_mobile ?? "").trim() &&
    !isValidMobileNumber(String(form.referrer_mobile), referrerCountry, {
      required: false,
    })
  ) {
    return "referrerMobileInvalid";
  }

  return null;
}

/** Maps VisitorFormFields inquiry state → visitor-inquiries API payload. */
export function buildVisitorInquiryPayload(
  form: Record<string, unknown>
): VisitorInquiryPayload {
  const validationError = validateVisitorInquiryForm(form);
  if (validationError) {
    throw new Error(VISITOR_INQUIRY_VALIDATION_MESSAGES[validationError]);
  }

  const visitedBefore =
    form.has_visited_before === true || form.has_visited_before === "true";

  const marriedStatus =
    form.married_status === true || form.married_status === "true";

  return {
    visit_date: String(form.date ?? "").trim(),
    visitor_name: String(form.name ?? "").trim(),
    date_of_birth: trimmed(form.date_of_birth),
    married_status: marriedStatus,
    marriage_anniversary_date: marriedStatus
      ? trimmed(form.marriage_anniversary_date)
      : undefined,
    age: optionalNumber(form.age),
    occupation: trimmed(form.occupation),
    mobile_number: trimmed(form.mobile_number),
    residence_phone: trimmed(form.residence_phone),
    email: trimmed(form.email),
    address: trimmed(form.address),
    business_address: trimmed(form.business_address),
    reason_for_visit: trimmed(form.reason_for_visit),
    visited_before: visitedBefore,
    reference_source: trimmed(form.reference_source),
    referrer_name: trimmed(form.referrer_name),
    referrer_mobile: trimmed(form.referrer_mobile),
  };
}
